/* ==========================================================================
   Facets — progressive enhancement for collection filtering and sorting.

   The native GET form is the mechanism; this module only makes it feel
   immediate. It never becomes the source of truth: the URL is. There is no
   client-side filter store, no router, and no facet or product markup built
   in JavaScript — Liquid renders every pixel, and this swaps regions of it.

   If anything here fails, or never loads, the page still filters: the form
   submits, the removal links navigate, the sort select posts.
   ========================================================================== */

const ROOT = '[data-facets-root]';

/* Regions replaced from one Section Rendering response. Swapping these rather
   than the whole section is what lets the filter panel stay open, keep its
   scroll position and keep focus while its contents update underneath. */
const REGIONS = ['controls', 'facets', 'facet-actions', 'grid'];

const liveRegion = () => document.getElementById('live-polite');

function sectionUrl(url, id) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', id);
  return u.toString();
}

/* The form's own state, minus the empties — an unset price bound must not
   appear in the URL as a parameter with no value. */
function urlFromForm(form) {
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (String(value).trim() !== '') params.append(key, value);
  }
  const query = params.toString();
  return form.getAttribute('action') + (query ? `?${query}` : '');
}

class Facets {
  constructor(root) {
    this.root = root;
    this.id = root.dataset.sectionId;
    this.form = root.querySelector('[data-facet-form]');
    this.pending = null;

    /* With JavaScript, change submits. */
    this.hideNoJsSubmits();

    if (this.form) {
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-sort]') || e.target.matches('input')) this.navigate(urlFromForm(this.form));
      });
      /* Typed price bounds settle before they are worth a request. */
      this.form.addEventListener('input', (e) => {
        if (e.target.type !== 'number') return;
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.navigate(urlFromForm(this.form)), 500);
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.navigate(urlFromForm(this.form));
      });
    }

    /* Removal links and Clear all are real hrefs; intercepting them keeps the
       panel open, and letting them through still works. */
    root.addEventListener('click', (e) => {
      const link = e.target.closest('.afilter, .facets__clear');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      this.navigate(link.href);
    });

    this.enhancePagination();

    window.addEventListener('popstate', () => this.render(window.location.href, false));
  }

  async navigate(url, push = true) {
    await this.render(url, push);
  }

  async render(url, push) {
    const token = {};
    this.pending = token;

    let html;
    try {
      const res = await fetch(sectionUrl(url, this.id));
      if (!res.ok) throw new Error(res.status);
      html = await res.text();
    } catch {
      /* Fail into the thing that always works rather than inventing an error
         surface. No toast, anywhere. */
      window.location.assign(url);
      return;
    }
    if (this.pending !== token) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');

    /* Remember what had focus inside a region about to be replaced, so the
       customer is not thrown back to the top of the panel mid-filtering. */
    const active = document.activeElement;
    const key = active && this.root.contains(active) && active.name
      ? `${active.name}|${active.value}`
      : null;

    REGIONS.forEach((name) => {
      const next = doc.querySelector(`[data-region="${name}"]`);
      const current = this.root.querySelector(`[data-region="${name}"]`);
      if (next && current) current.innerHTML = next.innerHTML;
    });

    if (key) {
      const [name, value] = key.split('|');
      const again = this.root.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
      if (again) again.focus({ preventScroll: true });
    }

    if (push) window.history.pushState({ facets: true }, '', url);

    this.hideNoJsSubmits();
    this.enhancePagination();
    this.announce();
  }

  /* The explicit submits exist for when this module is absent, so they leave
     both the layout and the tab order — never merely hidden from sight. They
     are re-hidden after every swap: a replaced region arrives as Liquid rendered
     it for a reader without JavaScript, Apply button and all. */
  hideNoJsSubmits() {
    this.root.querySelectorAll('[data-nojs-submit]').forEach((el) => { el.hidden = true; });
  }

  announce() {
    const region = liveRegion();
    const count = this.root.querySelector('.cbar__count');
    if (region && count) region.textContent = count.textContent.trim();
  }

  /* Native pagination is what ships. Where a next page exists, it is replaced
     with Load More, which appends rather than replaces — filters, sort and the
     products already on screen all survive. */
  enhancePagination() {
    const nav = this.root.querySelector('[data-pagination]');
    if (!nav) return;
    const next = nav.dataset.nextUrl;
    if (!next) { nav.remove(); return; }

    const wrap = document.createElement('div');
    wrap.className = 'load-more';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--secondary';
    btn.dataset.loadMore = '';
    btn.dataset.url = next;
    btn.dataset.loadingLabel = nav.dataset.loadingLabel || '';
    const span = document.createElement('span');
    span.textContent = nav.dataset.loadMoreLabel || '';
    btn.appendChild(span);
    wrap.appendChild(btn);
    nav.replaceWith(wrap);

    btn.addEventListener('click', () => this.loadMore(btn));
  }

  async loadMore(btn) {
    const url = btn.dataset.url;
    const label = btn.querySelector('span');
    const original = label.textContent;
    btn.disabled = true;
    label.textContent = btn.dataset.loadingLabel || 'Loading…';

    let html;
    try {
      const res = await fetch(sectionUrl(url, this.id));
      if (!res.ok) throw new Error(res.status);
      html = await res.text();
    } catch {
      window.location.assign(url);
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = this.root.querySelector('[data-grid]');
    const incoming = doc.querySelectorAll('[data-grid] > *');
    const before = grid.children.length;

    incoming.forEach((card) => grid.appendChild(document.importNode(card, true)));
    const firstNew = grid.children[before];

    const nextNav = doc.querySelector('[data-pagination]');
    const nextUrl = nextNav && nextNav.dataset.nextUrl;
    if (nextUrl) {
      btn.dataset.url = nextUrl;
      btn.disabled = false;
      label.textContent = original;
    } else {
      btn.closest('.load-more').remove();
    }

    /* Send the reader to the first product that just arrived rather than
       leaving them at a button whose page has grown beneath it. */
    const landing = firstNew && firstNew.querySelector('a[href]');
    if (landing) landing.focus({ preventScroll: true });
    this.announce();
  }
}

document.querySelectorAll(ROOT).forEach((root) => new Facets(root));
